import { useState } from "react";
import { usePaymentCards } from "@/hooks/use-payment-cards";
import { License, PaymentCard } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreditCard, Link, LinkIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface AssignCardDialogProps {
  license: License;
  buttonVariant?: "default" | "outline" | "ghost";
  buttonSize?: "default" | "sm" | "lg";
  children?: React.ReactNode;
}

export function AssignCardDialog({
  license,
  buttonVariant = "outline",
  buttonSize = "sm",
  children,
}: AssignCardDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const { cards, assignCardToLicenseMutation } = usePaymentCards();
  const { toast } = useToast();

  const handleAssignCard = async () => {
    if (!selectedCardId) {
      toast({
        title: "Error",
        description: "Please select a payment card",
        variant: "destructive",
      });
      return;
    }

    assignCardToLicenseMutation.mutate(
      {
        licenseId: license.id,
        paymentCardId: parseInt(selectedCardId),
      },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Payment card assigned to license",
          });
          setOpen(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant={buttonVariant} size={buttonSize}>
            <LinkIcon className="mr-2 h-4 w-4" /> Assign Payment Card
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assign Payment Card</DialogTitle>
          <DialogDescription>
            Select a payment card to assign to the license: <strong>{license.name}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <div className="text-sm font-medium">Select Payment Card</div>
            <Select onValueChange={setSelectedCardId} value={selectedCardId || undefined}>
              <SelectTrigger>
                <SelectValue placeholder="Select a payment card" />
              </SelectTrigger>
              <SelectContent>
                {cards.map((card) => (
                  <SelectItem key={card.id} value={card.id.toString()}>
                    <div className="flex items-center">
                      <CreditCard className="mr-2 h-4 w-4" />
                      {card.cardName} (•••• {card.lastFourDigits})
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignCard}
            disabled={assignCardToLicenseMutation.isPending}
          >
            {assignCardToLicenseMutation.isPending ? "Assigning..." : "Assign"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}